<template>
    <two-column-layout>
        <card-container>
            <h2>{{ $t(i18n.contact_info_step.title) }}</h2>
            <p>{{ $t(i18n.contact_info_step.description) }}</p>

            <text-input-fragment
              :label="$t(i18n.input.company_registration_number.label)"
              :description="$t(i18n.input.company_registration_number.description)"
              :placeholder="$t(i18n.input.company_registration_number.placeholder)"
              size="small"
              show-info
              :is-invalid="isFieldInvalid($v.form.coc) || isFieldInvalid($v.cocValidation)"
              v-model="form.coc"
              @input="setCoc"
              @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_COMPANY_REGISTRATION_NUMBER')"
            >
                <template #result v-if="!validating">
                    <p v-if="isCocValid">
                        <br>{{form.companyData.companyName}}<br>
                        {{form.companyData.companyStreetName}} {{form.companyData.companyHouseNumber}} <br>
                        {{form.companyData.companyPostalCode}} {{form.companyData.companyCityName}}
                    </p>
                    <info-block v-if="!$v.form.coc.$invalid && cocValidation.errorCode" variant="warning" icon="phone">
                        {{ $t(i18n.request_drop_out) }}
                    </info-block>
                    <field-error-messages :validation-state="$v.form.coc" />
                </template>
            </text-input-fragment>

            <div v-if="!cocValidation.errorCode">
                <radio-group-fragment
                  v-if="isCocValid"
                  key="sameRiskAddress"
                  :label="$t(i18n.input.same_risk_address.label)"
                  :description="$t(i18n.input.same_risk_address.description)"
                  group-name="same risk address"
                  :options="radioGroupYesNoOptions"
                  show-info
                  v-model="form.sameRiskAddress"
                  :is-invalid="isFieldInvalid($v.form.sameRiskAddress)"
                  @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_SAME_RISK_ADRESS')"
                >
                    <template #result>
                        <field-error-messages :validation-state="$v.form.sameRiskAddress" />
                        <field-error-messages :validation-state="$v.form.manualRiskAddress.streetName" />
                    </template>
                </radio-group-fragment>

                <address-input v-if="isCocValid && form.sameRiskAddress === 'n'"
                               :label-suffix="$t(i18n.input.risk_address.label)"
                               :result-label="$t(i18n.input.risk_address.label)"
                               :address="form.manualRiskAddress">
                </address-input>

                <fragment-separator></fragment-separator>

                <text-input-fragment
                  :label="$t(i18n.input.name.label)"
                  :description="$t(i18n.input.name.description)"
                  :placeholder="$t(i18n.input.name.placeholder)"
                  show-info
                  :is-invalid="isFieldInvalid($v.form.name)"
                  v-model="form.name"
                  @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_NAME')"
                >
                    <template #result>
                        <field-error-messages :validation-state="$v.form.name" />
                    </template>
                </text-input-fragment>

                <text-input-fragment
                  :label="$t(i18n.input.email.label)"
                  :description="$t(i18n.input.email.description)"
                  :placeholder="$t(i18n.input.email.placeholder)"
                  show-info
                  :is-invalid="isFieldInvalid($v.form.email)"
                  v-model="form.email"
                  @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_EMAIL')"
                >
                    <template #result>
                        <field-error-messages :validation-state="$v.form.email" />
                    </template>
                </text-input-fragment>

                <text-input-fragment
                  :label="$t(i18n.input.phone_number.label)"
                  :description="$t(i18n.input.phone_number.description)"
                  :placeholder="$t(i18n.input.phone_number.placeholder)"
                  size="small"
                  show-info
                  :is-invalid="isFieldInvalid($v.form.phoneNumber)"
                  v-model="form.phoneNumber"
                  @info-clicked="onInfoClicked('GPS_QUOTE_REQUEST_FORM_PHONE_NUMBER')"
                >
                    <template #result>
                        <field-error-messages :validation-state="$v.form.phoneNumber" />
                    </template>
                </text-input-fragment>
            </div>
        </card-container>
        <button-group direction="right" top-spacing="l">
            <button-fragment :disabled="validating" @click="nextStep()">{{$t(i18n.button.next)}}</button-fragment>
        </button-group>
    </two-column-layout>
</template>

<script>
    import {Utils} from "@bvm/core";
    import {
        and,
        email,
        maxLength,
        minLength,
        not,
        numeric,
        or,
        required,
        requiredIf
    } from 'vuelidate/lib/validators'
    import {
        TextInputFragment,
        FragmentSeparator,
        TwoColumnLayout,
        ButtonFragment,
        ButtonGroup,
        CardContainer,
        RadioGroupFragment,
        InfoBlock
    } from "@bvm/forms";
    import FieldErrorMessages from "../../shared/field-error-messages";
    import MediaItemModal from "../../shared/media-item-modal";
    import {createNamespacedHelpers} from "vuex";
    import {cocApi} from "../../../api/coc-api";
    import {GPS_POLICY_QUOTE_REQUEST} from "../../../config/funnel-id.constants"

    const { mapState, mapMutations } = createNamespacedHelpers(
      "gpsQuoteRequest"
    );

    const i18nBuilder = new Utils.I18n.I18nBuilder("gps_quote_request_form")
      .add("contact_info_step", b => b.add("title").add("description"))
      .add("button", b => b.add("next"))
      .add("radio_group", b => b.add("generic", c => c.add("yes").add("no")))
      .add("input", b => b
        .add("company_registration_number", c => c.add("label").add("description").add("placeholder"))
        .add("same_risk_address", c => c.add("label").add("description"))
        .add("name", c => c.add("label").add("description").add("placeholder"))
        .add("email", c => c.add("label").add("description").add("placeholder"))
        .add("phone_number", c => c.add("label").add("description").add("placeholder"))
        .add("risk_address", c => c.add("label"))
      );

    const i18nValidationBuilder = new Utils.I18n.I18nBuilder("validations_field")
      .add("coc", b => b
        .add("NO_COMPANY_FOUND")
        .add("TOO_MANY_REQUESTS")
        .add("COMPANY_NOT_ACCEPTABLE"))
      .add("minLength")
      .add("maxLength")
      .add("numeric")
      .add("required")
      .add("phone_number")
      .add("address_required");

    const i18nReusableBuilder = new Utils.I18n.I18nBuilder("reusable_label")
        .add("request_drop_out");

    export const messageKeys = [...i18nBuilder.getKeys(), ...i18nValidationBuilder.getKeys(), ...i18nReusableBuilder.getKeys()];

    export default {
        name: "company-profile-step",
        components: {
            FieldErrorMessages,
            TextInputFragment,
            FragmentSeparator,
            TwoColumnLayout,
            ButtonFragment,
            ButtonGroup,
            CardContainer,
            RadioGroupFragment,
            InfoBlock,
            "address-input": () => import("../../shared/address-input")
        },
        data() {
            return {
                i18n: {...i18nBuilder.getData(), ...i18nReusableBuilder.getData()},
                cocValidation: {},
                form: {
                    coc : null,
                    companyData: {
                        companyName: null,
                        companyStreetName: null,
                        companyHouseNumber: null,
                        companyPostalCode: null,
                        companyCityName: null,
                    },
                    sameRiskAddress: null,
                    manualRiskAddress: {
                      streetName: null,
                      houseNumber: null,
                      houseNumberAddition: null,
                      postalCode: null,
                      city: null,
                    },
                    name : null,
                    email : null,
                    phoneNumber : null
                },
                validating: false
            };
        },
        watch: {
          "form.sameRiskAddress"(value) {
            // Reset manual values when risk address is the same
            if (value !== "n") {
              this.form.manualRiskAddress.streetName = null;
              this.form.manualRiskAddress.houseNumber = null;
              this.form.manualRiskAddress.houseNumberAddition = null;
              this.form.manualRiskAddress.postalCode = null;
              this.form.manualRiskAddress.city = null;
            }
          }
        },
        computed: {
            ...mapState(["contactInfo", "storedCocValidation"]),
            radioGroupYesNoOptions() {
              return {
                y: this.$t(this.i18n.radio_group.generic.yes),
                n: this.$t(this.i18n.radio_group.generic.no)
              };
            },
            isCocValid() {
              return !this.$v.form.coc.$invalid && !this.$v.cocValidation.$invalid && this.form.companyData.companyName;
            }
        },
        methods: {
            ...mapMutations(["setContactInfo", "setCocValidation"]),
            onInfoClicked(mediaItemTag) {
                this.$bvmModal.show(MediaItemModal, {
                    itemTag: mediaItemTag
                }, {
                    clickToClose: true
                });
            },
            isFieldInvalid(field) {
                return field.$dirty && field.$invalid;
            },
            setCoc(value) {
                this.form.coc = value;
                if (!this.$v.form.coc.$invalid) {
                    this.getCompanyRdcData(value);
                }
            },
            getCompanyRdcData(newCocValue) {
                this.validating = true;
                this.form.companyData = {};
                cocApi(newCocValue, null, GPS_POLICY_QUOTE_REQUEST).then(validation => {
                    this.cocValidation = validation;
                    if (this.cocValidation.data) {
                        this.form.companyData = {
                            companyName : validation.data.name,
                            companyStreetName : validation.data.organizationAddress.streetName,
                            companyHouseNumber : validation.data.organizationAddress.houseNumber + (validation.data.organizationAddress.houseNumberAddition || ''),
                            companyPostalCode : validation.data.organizationAddress.postalCode,
                            companyCityName : (validation.data.organizationAddress.city || '').toLowerCase()
                        };
                    }
                }).finally(() => {
                    this.$v.form.coc.$touch();
                    this.$v.cocValidation.$touch();
                    this.validating = false;
                });
            },
            updateState() {
                this.setContactInfo(this.form);
                this.setCocValidation(this.cocValidation);
            },
            nextStep() {
                this.$v.$touch();
                if (this.$v.$invalid) {
                    this.$emit("onError");
                } else {
                    this.updateState();
                    this.$emit("nextStep");
                }
            }
        },
        created() {
            this.form = this.contactInfo;
            this.cocValidation = this.storedCocValidation;
        },
        validations() {
            return {
                form: {
                    coc: {
                        required,
                        numeric,
                        minLength: minLength(8),
                        maxLength: maxLength(8)
                    },
                    sameRiskAddress: {required},
                    name: {required},
                    email:  {required, email},
                    phoneNumber:  {
                      required,
                      phone_number: and(or(minLength(10), not(required)), maxLength(10), numeric)
                    },
                    manualRiskAddress: {
                      streetName: {
                        address_required: requiredIf(() => this.form.sameRiskAddress === 'n' )
                      }
                    }
                },
                cocValidation: {
                    data: {
                        ["coc_" + this.cocValidation.errorCode]: requiredIf(() => !this.$v.form.coc.$invalid )
                    }
                }
            }
        }
    }
</script>

<style>

</style>